import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { APP_NAME } from '../data/kitEnv';
import {
  getDropInfo,
  uploadFileToDrop,
  recordDropAsset,
  reportDropProgress,
  abortDrop,
  listResumableSessions,
  isDropConfigured,
  fingerprintOf,
  isDropFileDone,
  getDropFileDone,
  markDropFileDone,
  inspectGroup,
  saveFolderManifest,
  groupPartRelPath,
} from '../data/dropService';
import { groupFolderEntries, readDataTransfer, sanitizePath } from '../utils/folders';
import { deviceFingerprint } from '../utils/fingerprint';
import { createMonitor, makeSpeedSampler } from '../utils/transferMonitor';
import { runSpeedTest, connectionInfo } from '../utils/netProbe';
import UploadStats from '../components/UploadStats';

// How many staged items upload concurrently. Kept low because all parts hit one
// S3 origin (browser caps ~6 connections/origin); 2 is enough to hide per-file
// control-plane latency without splitting a big file's throughput.
const UPLOAD_PARALLELISM = 2;

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};
const fmtEta = (s) => {
  if (!Number.isFinite(s) || s <= 0) return '';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};
const kindOf = (file) => (file.type.startsWith('image/') ? 'photo' : 'file');
const commonPrefix = (keys) => {
  if (!keys.length) return '';
  const parts = keys[0].split('/');
  let depth = parts.length;
  for (const k of keys) {
    const ks = k.split('/');
    let d = 0;
    while (d < depth && d < ks.length && ks[d] === parts[d]) d++;
    depth = d;
  }
  const pre = parts.slice(0, depth).join('/');
  return pre ? `${pre}/` : '';
};

// Recursively walk a File System Access API directory handle into the same
// { file, relPath } shape as drag-drop / webkitdirectory, with the chosen
// folder name as the top path segment (so folder grouping + resume keys match).
async function walkDirHandle(handle, prefix) {
  const out = [];
  // eslint-disable-next-line no-restricted-syntax
  for await (const entry of handle.values()) {
    const rel = `${prefix}/${entry.name}`;
    if (entry.kind === 'file') {
      // eslint-disable-next-line no-await-in-loop
      out.push({ file: await entry.getFile(), relPath: rel });
    } else if (entry.kind === 'directory') {
      // eslint-disable-next-line no-await-in-loop
      out.push(...(await walkDirHandle(entry, rel)));
    }
  }
  return out;
}

let uid = 0;

// Module-scope so it's a STABLE component type — defining it inside DropUpload's
// render made React treat it as a new type on every state change (name typing,
// drag, staging), remounting the whole subtree (lost focus / blank flashes).
function Shell({ children, wide }) {
  return (
    <div className="cs-drop-wrap">
      <div className={`cs-drop-card${wide ? ' wide' : ''}`}>
        <div className="cs-drop-brand">
          {APP_NAME}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function DropUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState({ state: 'loading' });
  const [name, setName] = useState(() => localStorage.getItem('cs_drop_name') || '');
  const [passcode, setPasscode] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [drag, setDrag] = useState(false);
  const [items, setItems] = useState([]);
  const [online, setOnline] = useState(typeof navigator === 'undefined' || navigator.onLine !== false);
  const [resumable, setResumable] = useState([]);
  const [recognition, setRecognition] = useState([]);
  const [conn, setConn] = useState(() => connectionInfo());
  const [testing, setTesting] = useState(false);
  const [snap, setSnap] = useState(null);
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const controllers = useRef({});
  const itemsRef = useRef([]);
  itemsRef.current = items;
  // Staging: selected items wait here until the guest hits "Begin upload".
  const [started, setStarted] = useState(false);
  const startedRef = useRef(false);
  const pendingRef = useRef([]);
  const drainingRef = useRef(false);
  const monitor = useRef(createMonitor());
  const clientInfoRef = useRef(null); // browser fingerprint + GPS, captured once
  const nameRef = useRef(name);
  nameRef.current = name;
  const configured = isDropConfigured();

  useEffect(() => {
    let alive = true;
    if (!configured) {
      setInfo({ state: 'error', reason: 'unconfigured' });
      return undefined;
    }
    getDropInfo(token)
      .then((d) => {
        if (!alive) return;
        if (!d.ok) setInfo({ state: 'closed', reason: d.reason, ...d });
        else {
          setInfo({ state: 'ok', ...d });
          if (d.guestName && !localStorage.getItem('cs_drop_name')) setName(d.guestName);
        }
      })
      .catch((e) => alive && setInfo({ state: 'error', reason: e.status === 404 ? 'notfound' : 'error' }));
    return () => {
      alive = false;
    };
  }, [token, configured]);

  useEffect(() => {
    if (configured) setResumable(listResumableSessions(token));
  }, [token, configured]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const needsPasscode = info.state === 'ok' && info.requiresPasscode && !unlocked;

  const update = (id, patch) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  // Update one file inside a folder item's manifest (and optionally the folder
  // item itself) in a single render to keep large folders smooth.
  const updateManifest = (id, inner, partPatch, itemPatch) =>
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const manifest = (it.manifest || []).map((m) => (m.inner === inner ? { ...m, ...partPatch } : m));
        return { ...it, ...(itemPatch || {}), manifest };
      })
    );

  // Folders are expanded by default (more informative); this tracks the ones
  // the uploader has explicitly collapsed.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleExpand = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const anyActive = useMemo(() => items.some((it) => ['queued', 'uploading'].includes(it.status)), [items]);
  // Only run upload-time side effects (unload guard, wake-lock, telemetry) once
  // the guest has actually started — staged-but-not-yet-uploaded items shouldn't
  // trip a "leave site?" prompt.
  const transferring = started && anyActive;

  const overall = useMemo(() => {
    const active = items.filter((it) => ['queued', 'uploading', 'done'].includes(it.status));
    const total = active.reduce((s, it) => s + it.size, 0);
    const loaded = active.reduce((s, it) => s + (it.loaded || 0), 0);
    const uploading = items.filter((it) => it.status === 'uploading');
    const speed = uploading.reduce((s, it) => s + (it.speed || 0), 0);
    return {
      total,
      loaded,
      pct: total ? loaded / total : 0,
      speed,
      eta: speed > 0 ? (total - loaded) / speed : 0,
      activeCount: uploading.length,
      reconnectingCount: uploading.filter((it) => it.reconnecting).length,
      doneCount: items.filter((it) => it.status === 'done').length,
      n: active.length,
    };
  }, [items]);

  // Telemetry sampling while uploading. The interval depends only on `anyActive`
  // so it keeps a steady cadence — depending on `overall.loaded` would tear down
  // + recreate the timer on every progress event (many times/sec), so it would
  // almost never fire and the chart would stay empty. Throughput is read from
  // the monitor's wire-byte counter, so resume credits never spike it.
  useEffect(() => {
    if (!transferring) return undefined;
    // Sample right away so the chart starts drawing within ~1s, then keep going.
    const tick = () => {
      monitor.current.sampleThroughput();
      setSnap(monitor.current.snapshot());
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [transferring]);

  useEffect(() => {
    if (!transferring) return undefined;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [transferring]);

  useEffect(() => {
    if (!transferring || !('wakeLock' in navigator)) return undefined;
    let lock = null;
    let cancelled = false;
    const acquire = () => {
      if (document.visibilityState !== 'visible') return;
      navigator.wakeLock
        .request('screen')
        .then((l) => {
          if (cancelled) l.release().catch(() => {});
          else lock = l;
        })
        .catch(() => {});
    };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', acquire);
      lock?.release?.().catch(() => {});
    };
  }, [transferring]);

  const onName = (v) => {
    setName(v);
    localStorage.setItem('cs_drop_name', v);
  };

  const runTest = async () => {
    setTesting(true);
    try {
      setConn(await runSpeedTest());
    } finally {
      setTesting(false);
    }
  };

  // Auto-run one speed test once the page is usable so Ping / Upload aren't
  // blank "—" (the browser only volunteers Download + network type). Runs
  // before any big upload starts, so it doesn't compete for bandwidth.
  const autoTested = useRef(false);
  useEffect(() => {
    if (autoTested.current || !configured) return;
    if (info.state !== 'ok' || needsPasscode || !online) return;
    autoTested.current = true;
    runTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.state, needsPasscode, online, configured]);

  // Throttled live-progress heartbeat so the item owner sees in-flight uploads
  // appear in their media section. Best-effort; never blocks the transfer.
  //
  // Each beat is a Firestore transaction (read+write) on the owner's project, and
  // every active file beats independently — so a chatty cadence multiplied across
  // a batch can blow the project's daily write quota. We keep beats sparse: at
  // most once per BEAT_MS, and only when progress actually moved a meaningful
  // amount. Terminal beats (start / done / error / cancel) always go through.
  const BEAT_MS = 12 * 1000;
  const BEAT_MIN_DELTA = 0.02; // ≥2% of the file moved since the last beat
  const beatRef = useRef(new Map()); // key -> { t, loaded }
  const sendBeat = (key, fields, force) => {
    const now = Date.now();
    const last = beatRef.current.get(key) || { t: 0, loaded: -1 };
    if (!force) {
      if (now - last.t < BEAT_MS) return;
      const size = Number(fields.sizeBytes) || 0;
      const loaded = Number(fields.loaded) || 0;
      const moved = last.loaded < 0 || !size ? 1 : (loaded - last.loaded) / size;
      if (moved < BEAT_MIN_DELTA) return;
    }
    beatRef.current.set(key, { t: now, loaded: Number(fields.loaded) || last.loaded });
    reportDropProgress({ token, passcode, id: key, contributor: nameRef.current, ...fields });
  };

  const uploadLoose = async (id, file, relPath) => {
    const fp = fingerprintOf(file, relPath);
    const beatKey = `f|${fp}`;
    // Already finished on a previous visit → skip the bytes entirely.
    if (isDropFileDone(token, fp)) {
      update(id, { status: 'done', progress: 1, loaded: file.size, speed: 0, eta: 0, note: 'already uploaded' });
      return;
    }
    const controller = new AbortController();
    controllers.current[id] = controller;
    const speedOf = makeSpeedSampler();
    const beatFields = { name: file.name, relPath, sizeBytes: file.size, kind: kindOf(file), isFolder: false };
    try {
      update(id, { status: 'uploading', error: null, reconnecting: false });
      sendBeat(beatKey, { ...beatFields, loaded: 0, status: 'uploading' }, true);
      const { key } = await uploadFileToDrop(file, {
        token,
        passcode,
        relPath,
        contentType: file.type,
        signal: controller.signal,
        monitor: monitor.current,
        onState: (st) => update(id, { reconnecting: !!st.reconnecting }),
        onRecognized: (r) => update(id, { note: `resuming from part ${r.resumedParts}/${r.totalParts}` }),
        onProgress: (p) => {
          const loaded = p * file.size;
          const speed = speedOf(loaded);
          update(id, { progress: p, loaded, speed, eta: speed > 0 ? (file.size - loaded) / speed : 0 });
          sendBeat(beatKey, { ...beatFields, loaded, status: 'uploading' });
        },
      });
      await recordDropAsset({
        token,
        passcode,
        contributor: nameRef.current,
        clientInfo: clientInfoRef.current,
        asset: { name: file.name, sizeBytes: file.size, contentType: file.type, kind: kindOf(file), s3Key: key, status: 'stored' },
      });
      markDropFileDone(token, fp, { name: file.name, relPath, size: file.size, key });
      update(id, { status: 'done', progress: 1, loaded: file.size, speed: 0, eta: 0, reconnecting: false });
      sendBeat(beatKey, { status: 'done' }, true);
    } catch (err) {
      if (controller.signal.aborted) {
        update(id, { status: 'canceled', error: null, reconnecting: false });
        sendBeat(beatKey, { status: 'canceled' }, true);
        abortDrop(file, { token, relPath, passcode }).catch(() => {});
      } else {
        update(id, { status: 'error', error: err.message, reconnecting: false });
        sendBeat(beatKey, { ...beatFields, status: 'error' }, true);
      }
    } finally {
      delete controllers.current[id];
    }
  };

  const uploadGroup = async (id, group) => {
    const controller = new AbortController();
    controllers.current[id] = controller;
    const total = group.totalSize || 1;
    const speedOf = makeSpeedSampler();
    let base = 0;
    let currentRel = '';
    const parts = [];
    let primaryKey = '';
    const beatKey = `g|${group.folderName}|${group.files.length}`;
    const beatFields = { name: group.folderName, sizeBytes: total, kind: 'file', isFolder: true, fileCount: group.files.length };
    try {
      update(id, { status: 'uploading', error: null, reconnecting: false });
      sendBeat(beatKey, { ...beatFields, loaded: 0, status: 'uploading' }, true);
      for (const part of group.files) {
        const relPath = groupPartRelPath(group, part.inner);
        const fp = fingerprintOf(part.file, relPath);
        const prior = getDropFileDone(token, fp);
        if (prior?.key) {
          // Skip a part already uploaded on a previous visit.
          parts.push({ name: part.file.name, s3Key: prior.key, sizeBytes: part.file.size });
          if (part.inner === group.primaryInner) primaryKey = prior.key;
          base += part.file.size;
          updateManifest(id, part.inner, { status: 'done', progress: 1, skipped: true }, { progress: base / total, loaded: base });
          continue;
        }
        currentRel = relPath;
        updateManifest(id, part.inner, { status: 'uploading', progress: 0 });
        // eslint-disable-next-line no-await-in-loop
        const { key } = await uploadFileToDrop(part.file, {
          token,
          passcode,
          relPath,
          contentType: part.file.type || 'application/octet-stream',
          signal: controller.signal,
          monitor: monitor.current,
          onState: (st) => update(id, { reconnecting: !!st.reconnecting }),
          onProgress: (p) => {
            const loaded = base + p * part.file.size;
            const speed = speedOf(loaded);
            updateManifest(
              id,
              part.inner,
              { progress: p },
              { progress: loaded / total, loaded, speed, eta: speed > 0 ? (total - loaded) / speed : 0 }
            );
            sendBeat(beatKey, { ...beatFields, loaded, status: 'uploading' });
          },
        });
        markDropFileDone(token, fp, { name: part.file.name, relPath, size: part.file.size, key });
        parts.push({ name: part.file.name, s3Key: key, sizeBytes: part.file.size });
        if (part.inner === group.primaryInner) primaryKey = key;
        base += part.file.size;
        updateManifest(id, part.inner, { status: 'done', progress: 1 });
      }
      await recordDropAsset({
        token,
        passcode,
        contributor: nameRef.current,
        clientInfo: clientInfoRef.current,
        asset: {
          name: group.folderName,
          sizeBytes: total,
          contentType: '',
          kind: 'folder',
          s3Key: primaryKey || parts[0]?.s3Key,
          folderPrefix: commonPrefix(parts.map((p) => p.s3Key)),
          parts,
          status: 'stored',
        },
      });
      update(id, { status: 'done', progress: 1, loaded: total, speed: 0, eta: 0, reconnecting: false });
      sendBeat(beatKey, { status: 'done' }, true);
    } catch (err) {
      const cur = group.files.find((p) => groupPartRelPath(group, p.inner) === currentRel);
      if (controller.signal.aborted) {
        update(id, { status: 'canceled', error: null, reconnecting: false });
        sendBeat(beatKey, { status: 'canceled' }, true);
        if (cur) {
          updateManifest(id, cur.inner, { status: 'canceled' });
          abortDrop(cur.file, { token, relPath: currentRel, passcode }).catch(() => {});
        }
      } else {
        update(id, { status: 'error', error: err.message, reconnecting: false });
        sendBeat(beatKey, { ...beatFields, status: 'error' }, true);
        if (cur) updateManifest(id, cur.inner, { status: 'error' });
      }
    } finally {
      delete controllers.current[id];
    }
  };

  const runItem = (it) => (it.group ? uploadGroup(it.id, it.group) : uploadLoose(it.id, it.file, it.relPath));

  // Upload everything staged in the pending queue. A small pool of workers runs
  // a few items at once so the per-file control-plane round-trips (create / sign
  // / complete / record) pipeline instead of stalling the next file — a real win
  // for batches of smaller clips/photos. Big files still share the browser's
  // ~6-connections-per-origin budget, so this never oversubscribes the link.
  // Safe to call repeatedly: a single drain runs at a time and picks up
  // newly-added items.
  const drain = async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    const worker = async () => {
      while (pendingRef.current.length) {
        const it = pendingRef.current.shift();
        const cur = itemsRef.current.find((x) => x.id === it.id);
        if (cur && cur.status === 'canceled') continue;
        // eslint-disable-next-line no-await-in-loop
        await runItem(it);
      }
    };
    try {
      await Promise.all(Array.from({ length: UPLOAD_PARALLELISM }, worker));
    } finally {
      drainingRef.current = false;
    }
  };

  const beginUpload = () => {
    startedRef.current = true;
    setStarted(true);
    drain();
  };

  // Folder selection: prefer the File System Access API (a clean "select this
  // folder" dialog that returns the whole tree) and fall back to the
  // webkitdirectory <input> on browsers without it (Safari/Firefox).
  const chooseFolder = async () => {
    if (typeof window !== 'undefined' && window.showDirectoryPicker) {
      try {
        const dir = await window.showDirectoryPicker({ mode: 'read', id: 'kitDrop' });
        const entries = await walkDirHandle(dir, dir.name);
        if (entries.length) handleEntries(entries);
      } catch (_e) {
        /* guest dismissed the picker */
      }
    } else {
      folderRef.current?.click();
    }
  };

  const handleEntries = async (entries) => {
    if (!entries.length) return;
    setResumable([]);
    const { groups, loose } = groupFolderEntries(entries);

    // Recognize & remember folders for change detection.
    const msgs = [];
    groups.forEach((g) => {
      const r = inspectGroup(token, g);
      saveFolderManifest(token, g);
      if (r.recognized || r.done) {
        const bits = [`Recognized ${g.folderName} — ${r.total} files`];
        if (r.done) bits.push(`${r.done} already uploaded (skipping)`);
        if (r.changed) bits.push(`${r.changed} changed → re-uploading`);
        if (r.fresh && r.recognized) bits.push(`${r.fresh} new`);
        let m = `${bits.join(', ')}.`;
        if (r.missing.length) m += ` ${r.missing.length} file(s) from last time aren’t in this drop.`;
        msgs.push(m);
      }
    });
    const looseDone = loose.filter(({ file, relPath }) => isDropFileDone(token, fingerprintOf(file, sanitizePath(relPath)))).length;
    if (looseDone) msgs.push(`${looseDone} of ${loose.length} loose file(s) already uploaded — skipping.`);
    setRecognition(msgs);

    const groupItems = groups.map((g) => ({
      id: `u${uid++}`,
      group: g,
      name: g.folderName,
      size: g.totalSize,
      kind: 'folder',
      progress: 0,
      loaded: 0,
      speed: 0,
      eta: 0,
      status: 'queued',
      // Per-file manifest so the uploader sees exactly which files are queued,
      // sending, or finished. Already-uploaded files start checked off.
      manifest: g.files.map((p) => {
        const done = isDropFileDone(token, fingerprintOf(p.file, groupPartRelPath(g, p.inner)));
        return {
          inner: p.inner,
          name: p.file.name,
          size: p.file.size,
          status: done ? 'done' : 'queued',
          progress: done ? 1 : 0,
          skipped: done,
        };
      }),
    }));
    const looseItems = loose.map(({ file, relPath }) => ({
      id: `u${uid++}`,
      file,
      relPath: sanitizePath(relPath),
      name: file.name,
      size: file.size,
      kind: kindOf(file),
      progress: 0,
      loaded: 0,
      speed: 0,
      eta: 0,
      status: 'queued',
    }));
    const all = [...groupItems, ...looseItems];
    setItems((prev) => [...all, ...prev]);
    // Capture the guest's device fingerprint once per batch (no permission
    // prompt). The backend pairs it with the server-observed IP/geo-IP.
    if (!clientInfoRef.current) {
      clientInfoRef.current = { fingerprint: deviceFingerprint() };
    }
    // Stage the new items. They only start transferring once the guest presses
    // "Begin upload" — or immediately if a prior batch is already running.
    pendingRef.current.push(...all);
    if (startedRef.current) drain();
  };

  const handleFiles = (fileList) =>
    handleEntries(Array.from(fileList).map((f) => ({ file: f, relPath: f.webkitRelativePath || f.name })));

  const onDrop = async (e) => {
    e.preventDefault();
    setDrag(false);
    const entries = await readDataTransfer(e.dataTransfer);
    handleEntries(entries);
  };

  // Cancel an item: abort the in-flight transfer if it's uploading, otherwise
  // mark a still-queued item canceled so the runner loop skips it.
  const cancel = (id) => {
    const c = controllers.current[id];
    if (c) c.abort();
    else update(id, { status: 'canceled', error: null, reconnecting: false });
  };
  const cancelAll = () => {
    itemsRef.current.forEach((it) => {
      if (it.status === 'uploading' || it.status === 'queued') cancel(it.id);
    });
  };
  const retry = (it) => runItem(it);

  // Wire the directory-select attributes imperatively. A single <input> can't
  // offer BOTH files and folders, and React drops the non-standard `directory`
  // attribute — setting them on the live DOM node is the reliable cross-browser
  // way to make the OS dialog pick a whole folder.
  const setFolderInput = (el) => {
    folderRef.current = el;
    if (el) {
      el.setAttribute('webkitdirectory', '');
      el.setAttribute('directory', '');
      el.setAttribute('mozdirectory', '');
    }
  };

  // ---- Render states ------------------------------------------------------
  if (info.state === 'loading') {
    return (
      <Shell>
        <p className="cs-help">Loading…</p>
      </Shell>
    );
  }
  if (info.state === 'error' || info.state === 'closed') {
    const msg =
      info.reason === 'unconfigured'
        ? 'Uploads are not configured for this site yet.'
        : info.reason === 'notfound'
        ? "This upload link doesn't exist."
        : info.reason === 'expired'
        ? 'This upload link has expired. Ask for a fresh link.'
        : info.reason === 'revoked'
        ? 'This upload link has been turned off.'
        : 'This upload link is not available.';
    return (
      <Shell>
        <h1>Upload unavailable</h1>
        <p className="cs-help">{msg}</p>
      </Shell>
    );
  }

  return (
    <Shell wide={!needsPasscode && items.length > 0}>
      <h1>Upload files{info.itemName ? ` · ${info.itemName}` : ''}</h1>
      <p className="cs-help" style={{ marginTop: -6 }}>
        {info.ownerName ? `${info.ownerName} invited you to add files` : 'You were invited to add files'}. Any files
        or folders are welcome — big files (up to ~1 TB each) and whole folders upload in 100MB parts straight to
        secure storage.
      </p>
      {info.guestNote && (
        <p className="cs-help" style={{ marginTop: 4, color: 'var(--text)' }}>
          📝 {info.guestNote}
        </p>
      )}

      {!online && (
        <div className="cs-drop-offline">
          You’re offline — uploads paused. They’ll pick up right where they left off when you reconnect. Keep this tab
          open.
        </div>
      )}

      {needsPasscode ? (
        <div className="cs-drop-field">
          <label>
            Passcode
            <input
              className="cs-input"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Enter the passcode you were given"
            />
          </label>
          <button
            className="cs-btn cs-btn-primary"
            style={{ marginTop: 10 }}
            disabled={!passcode.trim()}
            onClick={() => setUnlocked(true)}
          >
            Continue
          </button>
        </div>
      ) : (
        <>
          {resumable.length > 0 && items.length === 0 && (
            <div className="cs-drop-resume">
              You have {resumable.length} interrupted upload{resumable.length === 1 ? '' : 's'} from before
              {resumable[0]?.name ? ` (e.g. ${resumable[0].name})` : ''}. Drop the same file{resumable.length === 1 ? '' : 's'}/folder
              again — already-uploaded parts are recognized and skipped automatically.
            </div>
          )}
          {recognition.map((m) => (
            <div className="cs-drop-resume" key={m}>
              ♻ {m}
            </div>
          ))}

          <div className={`cs-drop-layout${items.length > 0 ? ' split' : ''}`}>
            <div className="cs-drop-main">
          <div
            className={`cs-dropzone ${drag ? 'drag' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
          >
            <div style={{ fontSize: 32 }}>⬆</div>
            <div style={{ marginTop: 8, fontWeight: 600, color: 'var(--text)' }}>
              Drop files or folders here, or choose below
            </div>
            <div style={{ marginTop: 4, fontSize: 13 }}>
              50GB+ files are fine — they survive Wi-Fi drops and resume automatically. Keep this tab open.
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
                title="Pick one or more files"
              >
                📄 Choose files
              </button>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  chooseFolder();
                }}
                title="Pick a whole folder, keeping its structure"
              >
                📁 Choose a folder
              </button>
            </div>
            <input ref={inputRef} type="file" multiple hidden onChange={(e) => e.target.files && handleFiles(e.target.files)} />
            <input ref={setFolderInput} type="file" multiple hidden onChange={(e) => e.target.files && handleFiles(e.target.files)} />
          </div>

          <label className="cs-drop-field" style={{ marginTop: 14 }}>
            Your name <span className="cs-help" style={{ fontWeight: 400 }}>(optional — so we can credit your uploads)</span>
            <input className="cs-input" value={name} onChange={(e) => onName(e.target.value)} placeholder="e.g. Carlos" />
          </label>

          {overall.n > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14 }}>
                  {!started
                    ? `Ready — ${overall.n} item${overall.n === 1 ? '' : 's'} staged`
                    : overall.reconnectingCount > 0
                    ? 'Reconnecting…'
                    : overall.activeCount > 0
                    ? `Uploading ${overall.doneCount}/${overall.n}`
                    : `${overall.doneCount}/${overall.n} uploaded`}
                </strong>
                <span className="cs-time">
                  {fmtBytes(overall.loaded)} / {fmtBytes(overall.total)}
                  {overall.speed > 0 && overall.reconnectingCount === 0 && ` · ${fmtBytes(overall.speed)}/s · ${fmtEta(overall.eta)} left`}
                </span>
                {!started ? (
                  <button
                    className="cs-btn cs-btn-primary cs-btn-sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={beginUpload}
                    disabled={overall.n === 0}
                    title="Start uploading the staged files"
                  >
                    Begin upload
                  </button>
                ) : anyActive ? (
                  <button
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={cancelAll}
                    title="Stop all uploads in progress and waiting"
                  >
                    Cancel all
                  </button>
                ) : null}
              </div>
              <div className="cs-progress" style={{ height: 8 }}>
                <div style={{ width: `${Math.round(overall.pct * 100)}%` }} />
              </div>
              {overall.activeCount === 0 && overall.doneCount === overall.n && (
                <p className="cs-help" style={{ marginTop: 10 }}>
                  All done — thank you! You can close this tab, or drop more files above.
                </p>
              )}
            </div>
          )}

          {overall.n > 0 && (
            <UploadStats
              snapshot={snap}
              conn={conn}
              testing={testing}
              onTest={runTest}
              online={online}
              liveBps={overall.speed}
            />
          )}
            </div>

            {items.length > 0 && (
              <aside className="cs-drop-side">
                <div className="cs-drop-side-head">
                  <span>Files</span>
                  <span className="cs-us-dim">
                    {overall.doneCount}/{overall.n} done
                  </span>
                </div>
                <div className="cs-drop-side-list">
              {items.map((it) => {
                const manifest = it.manifest || [];
                const isFolder = manifest.length > 0;
                const doneFiles = manifest.filter((m) => m.status === 'done').length;
                const isOpen = !collapsed.has(it.id);
                const pct = Math.round((it.progress || 0) * 100);
                return (
                  <div className="cs-upload-block" key={it.id}>
                    <div className="cs-upload-item">
                      {isFolder ? (
                        <button
                          className="cs-manifest-toggle"
                          onClick={() => toggleExpand(it.id)}
                          aria-label={isOpen ? 'Collapse files' : 'Expand files'}
                          title={isOpen ? 'Hide files' : 'Show files'}
                        >
                          {isOpen ? '▾' : '▸'}
                        </button>
                      ) : (
                        <span className="cs-manifest-toggle is-empty" />
                      )}
                      <div className="cs-ui-name" title={it.name}>
                        {it.status === 'done' && <span className="cs-check">✓</span>}
                        {it.kind === 'photo' ? '🖼' : it.kind === 'folder' ? '📁' : '📄'} {it.name}
                        {isFolder && (
                          <span className="cs-us-dim" style={{ marginLeft: 6, fontSize: 12 }}>
                            {doneFiles}/{manifest.length}
                          </span>
                        )}
                      </div>
                      {it.status === 'uploading' || it.status === 'queued' ? (
                        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => cancel(it.id)}>
                          Cancel
                        </button>
                      ) : it.status === 'error' || it.status === 'canceled' ? (
                        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => retry(it)}>
                          {it.status === 'error' ? 'Retry' : 'Restart'}
                        </button>
                      ) : null}
                    </div>
                    <div className="cs-ui-meta">
                      <span className="cs-ui-size">{fmtBytes(it.size)}</span>
                      <div className="cs-progress cs-ui-bar">
                        <div style={{ width: `${pct}%` }} />
                      </div>
                      <span
                        className="cs-ui-status"
                        title={it.error || it.note || ''}
                        style={it.status === 'error' ? { color: 'var(--accent)' } : undefined}
                      >
                        {it.status === 'uploading' && it.reconnecting
                          ? 'reconnecting…'
                          : it.status === 'uploading'
                          ? `${pct}%${it.speed ? ` · ${fmtBytes(it.speed)}/s` : ''}`
                          : it.status === 'done'
                          ? it.note || 'complete'
                          : it.status}
                      </span>
                    </div>
                    {isFolder && isOpen && (
                      <div className="cs-manifest">
                        {manifest.map((m) => (
                          <div className={`cs-manifest-row st-${m.status}`} key={m.inner}>
                            <span className="cs-manifest-ic">
                              {m.status === 'done' ? '✓' : m.status === 'uploading' ? '↑' : m.status === 'error' ? '✕' : m.status === 'canceled' ? '–' : '•'}
                            </span>
                            <span className="cs-manifest-name" title={m.inner}>
                              {m.name}
                              {m.skipped && <span className="cs-us-dim"> · already uploaded</span>}
                            </span>
                            <span className="cs-manifest-size">{fmtBytes(m.size)}</span>
                            <div className="cs-progress cs-manifest-bar">
                              <div style={{ width: `${Math.round((m.progress || 0) * 100)}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
              </aside>
            )}
          </div>
        </>
      )}
    </Shell>
  );
}
